import { useState } from "react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { Priority, Habit } from "@/types/habit";
import { HabitCardHeader } from "./HabitCardHeader";
import { HabitCardProgress } from "./HabitCardProgress";
import { HabitCardActions } from "./HabitCardActions";
import { HabitCardReward } from "./HabitCardReward";
import { HabitCardCategories } from "./HabitCardCategories";
import { HabitProgressDialog } from "./HabitProgressDialog";
import { HabitEditPopover } from "./HabitEditPopover";
import { triggerConfetti } from "@/utils/habitUtils";

interface HabitCardProps {
  habit: Habit;
  onComplete: (id: string, dailyProgress?: number) => void;
  onDuplicate?: (id: string) => void;
  onRevert?: (id: string) => void;
  onDelete: (id: string) => void;
  onUpdateCategories: (id: string, categories: string[]) => void;
  onUpdatePriority: (id: string, priority: Priority) => void;
  onUpdateTitle?: (id: string, title: string) => void;
  onUpdateDailyTarget?: (id: string, value: number, unit: string) => void;
  availableCategories: string[];
  handleAddNewCategoryToSystem: (category: string, icon?: any) => void;
}

export const HabitCard = ({ 
  habit, 
  onComplete, 
  onDuplicate, 
  onRevert, 
  onDelete,
  onUpdateCategories,
  onUpdatePriority,
  onUpdateTitle,
  onUpdateDailyTarget,
  availableCategories,
  handleAddNewCategoryToSystem,
}: HabitCardProps) => {
  const [isProgressDialogOpen, setIsProgressDialogOpen] = useState(false);
  const { toast } = useToast();

  const priorityColors = {
    high: "border-l-purple-800", // Dark purple for high priority
    medium: "border-l-purple-500", // Medium purple for medium priority  
    low: "border-l-purple-300", // Light purple for low priority
  };

  const handleComplete = () => {
    if (habit.dailyTarget) {
      setIsProgressDialogOpen(true);
      // Pre-fill with remaining amount needed
      if (habit.dailyProgress !== undefined) {
        const remaining = habit.dailyTarget.value - habit.dailyProgress;
        // This will be handled in the dialog component
      }
    } else {
      onComplete(habit.id);
      triggerConfetti();
    }
  };

  const handleProgressSubmit = (progress: number) => {
    // Calculate new total progress
    const currentProgress = habit.dailyProgress || 0;
    const newTotal = currentProgress + progress;
    
    // Submit the progress
    onComplete(habit.id, progress);
    
    if (habit.dailyTarget) {
      if (newTotal < habit.dailyTarget.value) {
        const remaining = habit.dailyTarget.value - newTotal;
        toast({
          title: "Progress saved",
          description: `${remaining.toFixed(1)} ${habit.dailyTarget.unit} remaining to reach your goal`,
        });
      } else {
        toast({
          title: "Daily target met!",
          description: "Great job! Progress will reset for tomorrow.",
        });
        triggerConfetti();
      }
    }
    
    setIsProgressDialogOpen(false);
  };

  return (
    <>
      <Card
        className={cn(
          "relative overflow-hidden transition-all duration-300 hover:shadow-lg bg-white/80 backdrop-blur-sm border-l-4 w-full",
          priorityColors[habit.priority]
        )}
      >
        <div className="absolute top-2 right-2 flex items-center gap-2">
          <HabitEditPopover
            habit={habit}
            availableCategories={availableCategories}
            onUpdateTitle={onUpdateTitle}
            onUpdatePriority={onUpdatePriority}
            onUpdateCategories={onUpdateCategories}
            onUpdateDailyTarget={onUpdateDailyTarget}
            onDelete={onDelete}
            handleAddNewCategoryToSystem={handleAddNewCategoryToSystem}
          />
        </div>

        <div className="p-6 space-y-4">
          <HabitCardHeader 
            title={habit.title} 
            emoji={habit.emoji} 
            priority={habit.priority} 
          />

          <HabitCardProgress 
            completedDays={habit.completedDays}
            totalDays={habit.totalDays}
            dailyTarget={habit.dailyTarget}
            dailyProgress={habit.dailyProgress}
          />

          <div className="flex items-center justify-between">
            <HabitCardReward reward={habit.reward} rewardImage={habit.rewardImage} />
            
            <HabitCardActions
              isCompleted={habit.isCompleted}
              completedDays={habit.completedDays}
              onComplete={handleComplete}
              onRevert={() => onRevert?.(habit.id)}
              onDuplicate={() => onDuplicate?.(habit.id)}
            />
          </div>

          <HabitCardCategories categories={habit.category} />
        </div>
      </Card>

      <HabitProgressDialog
        open={isProgressDialogOpen}
        onOpenChange={setIsProgressDialogOpen}
        dailyTarget={habit.dailyTarget}
        dailyProgress={habit.dailyProgress}
        onSubmit={handleProgressSubmit}
      />
    </>
  );
};
